import { ApplicationState } from "./../../index";
import { IAddOn } from "../../AddOn";
import { Coverage } from "../../Insurance";
import {
  mapAddonsWithContentInState,
  mapCoverageDescription,
  mapCoverageItemsInCoverage,
  mapCoveragePrice,
  mapDeductibles,
  prepareAddons,
  preparePrice,
} from "./init";
import { FooterState, GetUpdatedFooterPricesV2 } from "../../Footer";
import { DistanceValue } from "../../Distance";
import { toErrorPayload } from "../../../helpers/errorPayload";
import { setServerError } from "../../error";
import { SERVER_ERROR_MESSAGE, API_HOST_ERROR_MESSAGE } from "../../../constants/errorMessages";

const method = "POST";
const headers = { "Content-Type": "application/json" };

const Calculate = async (state: ApplicationState, dispatch?: Function): Promise<ApplicationState> => {
  if (!state.inputs.apiHost) {
    if (dispatch) dispatch(setServerError(API_HOST_ERROR_MESSAGE, SERVER_ERROR_MESSAGE));
    throw toErrorPayload({ message: API_HOST_ERROR_MESSAGE, header: SERVER_ERROR_MESSAGE });
  }

  const url = `${state.inputs.apiHost}/api/se/calculate`;

  // prepare params;
  const cid = state.insurance?.coverage?.selectedCode || "";

  //remove not allowed addons for selected coverage
  const selectedCoverage = state?.insurance?.coverage?.Coverages?.find(item => item.InternalCode === cid);
  const addons =
    selectedCoverage?.CoverageAddons?.filter((element: any) => state?.addon?.selectedAddOnInternalCodes.includes(element.InternalCode)) ??
    [];
  const allowedSelectedAddons: string[] = [];
  addons.forEach((addon: any) => {
    allowedSelectedAddons.push(addon.InternalCode);
  });

  const CoverageDeductiblePresent = state.insurance?.coverage?.Coverages?.find(x => x.InternalCode === cid)?.Deductibles?.length;

  // todo: replace/improve;
  const data: any = {
    quoteId: state.inputs.quoteId,
    selectedCoverageId: cid,
    selectedAddonCoverageIds: allowedSelectedAddons ?? [],
    selectedDeductibleId: CoverageDeductiblePresent ? state.insurance?.coverage?.SelectedDeductible : null,
    selectedMileageOptionId: state.distance?.selected || "0",
    hasHadAccident: state.damageFreeYear?.selected,
    selectedPaymentMethodAndFrequencyId: state.payment?.selectedPaymentTypeCode,
    brand: state.inputs.brand,
  };

  const response = await fetch(`${url}`, {
    method,
    mode: "cors",
    headers,
    body: JSON.stringify(data),
  });

  const json = await response.json();

  if (response.status !== 200) {
    const message = (json && json.message) ?? SERVER_ERROR_MESSAGE;
    if (dispatch) dispatch(setServerError(message, SERVER_ERROR_MESSAGE));
    throw toErrorPayload({ message, header: SERVER_ERROR_MESSAGE });
  }

  const coverages = json.coverages?.map(
    (val: any) =>
      ({
        InternalCode: val.id,
        LongName: val.name,
        ShortName: val.name.substring(0, 3),
        Description: mapCoverageDescription(
          val,
          state?.texts?.coverageDescriptions,
          json?.vehicle?.make,
          json?.vehicle?.vehicleTypeCode,
          json.partner?.id,
        ),
        PrimaryPrice: mapCoveragePrice(val.price),
        SecondaryPrice: {},
        IsRecommended: val.isSelected,
        CoverageItems: mapCoverageItemsInCoverage(json.coverageItems, val.includedCoverageItems),
        Deductibles: mapDeductibles(val.deductibles),
        CoverageAddons: mapAddonsWithContentInState(val.addons, state?.addon?.addOns),
        CalculatedPrice: preparePrice(val.price),
        CalculatedPriceSecondary: "",
      } as Coverage),
  );

  const currentCoverage = coverages.find((x: any) => x.IsRecommended === true);
  const selectedAddonInternalCodes =
    currentCoverage.CoverageAddons?.filter((x: any) => x.Checked)
      ?.map((x: IAddOn) => x.InternalCode)
      ?.filter((x?: string) => x != null) ?? [];

  const currentCoverageItems = currentCoverage.CoverageItems;

  const mileages: DistanceValue[] =
    json.mileage?.availableMileageOptions?.map((i: any) => ({
      title: i.description,
      value: i.value,
    })) ?? [];

  const footerPrices = GetUpdatedFooterPricesV2(state.footer as FooterState, currentCoverage, selectedAddonInternalCodes);

  const addOns = prepareAddons(coverages, selectedAddonInternalCodes);

  return {
    ...state,
    addon: {
      ...state.addon,
      addOns: addOns,
      selectedAddOnInternalCodes: selectedAddonInternalCodes,
    },
    distance: {
      ...state.distance,
      values: mileages,
      selected: json.mileage?.mileageUsedInCalculationInKilometers,
    },
    insurance: {
      ...state.insurance,
      coverage: {
        ...state.insurance?.coverage,
        selectedCode: currentCoverage.InternalCode,
        selectedName: currentCoverage.Name,
        Coverages: coverages,
      },
    },
    footer: {
      ...state.footer,
      ...footerPrices,
      expanded: true,
    },
    singleRecomendation: {
      ...state.singleRecomendation,
      coverageItems: currentCoverageItems,
    },
  } as ApplicationState;
};

export default Calculate;
